import React, { useEffect, useRef, useState } from 'react';
import InstallPrompt from '../common/InstallPrompt';
import { Outlet, useParams, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TenantProvider, useTenant } from '../../contexts/TenantContext';
import { useAuthStore } from '../../store/authStore';
import { agentsAPI } from '../../services/api';
import Sidebar from './Sidebar';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import SuperadminImpersonationBanner from './SuperadminImpersonationBanner';
import { unloadServitiTeamSupport } from '../../utils/servitiTeamChat';
import ServitiLogo from '../common/ServitiLogo';
import { documentTitleFor } from '../../utils/pageTitles';
import {
  Bars3Icon,
  HomeIcon,
  CpuChipIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';

/**
 * TenantLayout wraps all tenant-specific routes.
 * URL pattern: /t/:tenantId/*
 *
 * Responsibilities:
 * - Extract tenantId from URL
 * - Validate user can access this tenant
 * - Provide TenantContext to all child routes
 * - Render sidebar with tenant-aware navigation
 */
// Shown greyed out during first-time setup, in the sidebar's own words.
const SETUP_PREVIEW_ITEMS = [
  'Dashboard', 'Leads', 'Conversations', 'Support tickets', 'My agents',
  'Business knowledge', 'Insights', 'Lead alerts', 'Billing & payments',
];

// One title per page, so tabs, history and bookmarks can be told apart.
const useDocumentTitle = (pathname) => {
  useEffect(() => {
    document.title = documentTitleFor(pathname);
  }, [pathname]);
};

const TenantLayoutInner = ({ isPlatformAdmin }) => {
  const { tenantId } = useParams();
  const location = useLocation();
  useDocumentTitle(location.pathname);
  const navigate = useNavigate();
  const { user, isAuthenticated, isInitialized, switchTenant, logout } = useAuthStore();
  const tenant = useTenant();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tenantBindingError, setTenantBindingError] = useState(null);
  const [onboardingCheck, setOnboardingCheck] = useState({
    tenantId: null,
    status: 'checking',
    error: null,
  });
  const onboardingRequestRef = useRef(0);

  const isSuperadminActor = Boolean(
    user?.role === 'superadmin' || user?.is_superuser || user?.is_superadmin_actor
  );
  const needsTenantBinding = Boolean(
    !isPlatformAdmin &&
    isSuperadminActor &&
    tenantId &&
    tenantId !== '00000000-0000-0000-0000-000000000000' &&
    (user?.tenant_id !== tenantId || !user?.tenant_admin_mode)
  );
  const canCompleteOnboarding = Boolean(
    user?.tenant_role === 'owner' ||
    user?.tenant_role === 'admin' ||
    (user?.role === 'admin' && user?.tenant_id)
  );

  // Entering /t/:tenantId is a real security-context switch, not just a URL
  // change. The API returns a tenant-scoped token whose effective role is
  // tenant admin while retaining the real superadmin actor in signed claims.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !needsTenantBinding) return;

    let cancelled = false;
    setTenantBindingError(null);
    switchTenant(tenantId, { sessionOnly: true })
      .then((data) => {
        if (!cancelled && !data?.user?.tenant_admin_mode) {
          setTenantBindingError(
            'Tenant Admin mode is not available on the current API deployment.'
          );
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setTenantBindingError(
          error?.response?.data?.detail || 'Could not open this tenant workspace.'
        );
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, isInitialized, needsTenantBinding, switchTenant, tenantId]);

  // The support widget is loaded on demand by the tenant-admin sidebar. Tear
  // that runtime down when this layout closes or its account context changes,
  // so a later workspace can never inherit the previous tenant's transcript
  // or ticket metadata.
  useEffect(() => () => {
    unloadServitiTeamSupport();
  }, [tenantId, user?.email, user?.tenant_name, user?.username]);

  const checkOnboarding = React.useCallback(async () => {
    const requestId = onboardingRequestRef.current + 1;
    onboardingRequestRef.current = requestId;
    if (isPlatformAdmin || isSuperadminActor || !tenantId || needsTenantBinding) return;

    setOnboardingCheck({ tenantId, status: 'checking', error: null });
    try {
      const response = await agentsAPI.getOnboardingStatus();
      if (onboardingRequestRef.current !== requestId) return;
      setOnboardingCheck({
        tenantId,
        status: response.data?.onboarding_complete ? 'complete' : 'incomplete',
        error: null,
      });
    } catch (error) {
      if (onboardingRequestRef.current !== requestId) return;
      setOnboardingCheck({
        tenantId,
        status: 'error',
        error: error?.response?.data?.detail || 'Could not verify this workspace setup.',
      });
    }
  }, [isPlatformAdmin, isSuperadminActor, needsTenantBinding, tenantId]);

  useEffect(() => {
    checkOnboarding();
    return () => { onboardingRequestRef.current += 1; };
  }, [checkOnboarding]);

  useEffect(() => {
    const markComplete = (event) => {
      if (String(event?.detail?.tenantId || '') !== String(tenantId || '')) return;
      onboardingRequestRef.current += 1;
      setOnboardingCheck({ tenantId, status: 'complete', error: null });
    };
    window.addEventListener('serviti:onboarding-complete', markComplete);
    return () => window.removeEventListener('serviti:onboarding-complete', markComplete);
  }, [tenantId]);

  const basePath = isPlatformAdmin ? '/platform' : `/t/${tenantId}`;
  const bottomTabs = [
    { name: 'Home', icon: HomeIcon, path: `${basePath}/dashboard` },
    { name: 'Agents', icon: CpuChipIcon, path: `${basePath}/agent-management` },
    { name: 'More', icon: EllipsisHorizontalIcon, action: () => setSidebarOpen(true) },
  ];
  const isTabActive = (tab) => tab.path && location.pathname.startsWith(tab.path);

  // Wait for auth state to initialize before making decisions
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated, redirect to auth
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // Never render tenant pages with the previous platform/tenant token. That
  // brief mixed state was the source of wrong-scope requests and inconsistent
  // superadmin-vs-tenant-admin screens.
  if (needsTenantBinding) {
    if (tenantBindingError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-red-600 dark:text-red-400">{tenantBindingError}</p>
          <button
            type="button"
            onClick={() => window.close()}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Close This Tab
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // For platform admin routes, skip tenant validation
  if (!isPlatformAdmin) {
    // Validate tenant access
    if (!tenant.canAccess) {
      console.warn(`User ${user?.username} cannot access tenant ${tenantId}`);
      // Redirect to user's own tenant
      if (user?.tenant_id) {
        return <Navigate to={`/t/${user.tenant_id}/dashboard`} replace />;
      }
      return <Navigate to="/auth/login" replace />;
    }
  }

  const onboardingStatus = onboardingCheck.tenantId === tenantId
    ? onboardingCheck.status
    : 'checking';
  const setupPath = `/t/${tenantId}/get-started`;
  const onSetupPath = location.pathname.replace(/\/$/, '') === setupPath;
  const creatingAnotherAgent = onSetupPath && new URLSearchParams(location.search).get('create') === '1';
  const signOut = () => {
    logout();
    navigate('/auth/login', { replace: true });
  };

  if (!isPlatformAdmin && !isSuperadminActor) {
    if (onboardingStatus === 'checking') {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Checking workspace setup...</p>
          </div>
        </div>
      );
    }

    if (onboardingStatus === 'error') {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-center dark:bg-gray-900">
          <ServitiLogo alt="Serviti" className="h-12" />
          <h1 className="text-xl font-bold text-gray-950 dark:text-white">We could not open your setup</h1>
          <p className="max-w-md text-sm text-red-700 dark:text-red-300">{onboardingCheck.error}</p>
          <button type="button" onClick={checkOnboarding} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
            Try again
          </button>
          <button type="button" onClick={signOut} className="text-sm font-semibold text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
            Sign out
          </button>
        </div>
      );
    }

    if (onboardingStatus === 'incomplete') {
      if (!canCompleteOnboarding) {
        return (
          <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-50 px-6 text-center dark:bg-gray-900">
            <ServitiLogo alt="Serviti" className="h-12" />
            <h1 className="text-xl font-bold text-gray-950 dark:text-white">Workspace setup is not finished</h1>
            <p className="max-w-md text-sm text-gray-600 dark:text-gray-300">Ask a workspace admin to finish creating the first AI agent.</p>
            <button type="button" onClick={signOut} className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-300">
              Sign out
            </button>
          </div>
        );
      }
      if (!onSetupPath) return <Navigate to={setupPath} replace />;

      // Keep incomplete setup distraction-free. The normal sidebar, workspace
      // switcher and mobile tabs cannot be used to wander into empty screens.
      return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
          <header className="border-b border-gray-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ServitiLogo alt="Serviti" className="h-9" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Finish setting up your AI Agent</span>
              </div>
              <button type="button" onClick={signOut} className="text-sm font-semibold text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                Sign out
              </button>
            </div>
            {/* The menu stays locked until there is knowledge and an agent:
                empty screens were how new sign-ups got lost. Show what it
                holds, so nothing looks missing. */}
            <div className="mx-auto mt-2 max-w-5xl">
              <p className="text-xs text-gray-500 dark:text-gray-400">Opens when your first agent is ready:</p>
              <ul aria-label="Opens after setup" className="mt-1 flex flex-wrap gap-1.5">
                {SETUP_PREVIEW_ITEMS.map((label) => (
                  <li
                    key={label}
                    className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
                  >
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </header>
          <main className="dark-content"><Outlet /></main>
        </div>
      );
    }

    if (onboardingStatus === 'complete' && onSetupPath && !creatingAnotherAgent) {
      // /get-started is also the login landing route (AuthPage sends every
      // workspace here), so a finished workspace goes on to its dashboard.
      // "Create an agent" in the menu opens setup again with ?create=1.
      return <Navigate to={`/t/${tenantId}/dashboard`} replace />;
    }
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar
        isOpen={sidebarOpen}
        isPlatformAdmin={isPlatformAdmin}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Superadmin impersonation banner — self-hides for non-superadmins,
            platform routes, and the reserved platform tenant. */}
        {!isPlatformAdmin && <SuperadminImpersonationBanner />}

        {/* Mobile header bar */}
        <div className="lg:hidden flex items-center h-14 px-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <ServitiLogo alt="Serviti" className="ml-3 h-8" />
          <div className="ml-auto flex items-center gap-2">
            <WorkspaceSwitcher />
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
          </div>
        </div>

        {/* Desktop-only top bar: only renders the workspace switcher when the
            user has 2+ memberships (the component self-hides otherwise) */}
        <div className="hidden lg:flex items-center justify-end px-6 pt-3 pb-1 bg-gray-100 dark:bg-gray-900">
          <WorkspaceSwitcher />
        </div>

        <InstallPrompt />
        <main className="dark-content flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900 pb-16 lg:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom tab bar */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-30 flex items-center justify-around h-16 safe-area-bottom">
          {bottomTabs.map((tab) => {
            const active = isTabActive(tab);
            return (
              <button
                key={tab.name}
                onClick={() => tab.action ? tab.action() : navigate(tab.path)}
                className={`flex flex-col items-center justify-center flex-1 h-full min-h-[44px] transition-colors ${
                  active
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-6 h-6" />
                <span className="text-xs mt-1">{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

// Outer component wraps with TenantProvider
const TenantLayout = ({ isPlatformAdmin = false }) => {
  return (
    <TenantProvider>
      <TenantLayoutInner isPlatformAdmin={isPlatformAdmin} />
    </TenantProvider>
  );
};

export default TenantLayout;
